'use client';

import { TermsDict } from '@/lib/dictionaries';
 import Image from 'next/image';
 import { ChevronRight } from 'lucide-react'; // ✅ ajout de l’icône
import { useEffect, useState } from 'react';
import './TermsUI.css'; // <-- Import du CSS custom

import dynamic from "next/dynamic";
const NavBar = dynamic(() => import('@/app/components/navBar/NavBar'), { ssr: false });

const Breadcrumb = dynamic(() => import('@/app/components/ui/Breadcrumb'), {
  ssr: false,
  loading: () => <div className="text-sm text-gray-400">Chargement...</div>,
});

interface TermsPageUIProps {
  dict: TermsDict;
  locale: string;
}

export default function TermsPageUI({ dict, locale }: TermsPageUIProps) {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      let current: string | null = null;
      dict.sections.forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            current = section.id;
          }
        }
      });
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [dict.sections]);

  return (
    <>
      {/* ===== HEADER ===== */}
      <section className="min-h-[35vh] bg-[#330052] text-white font-sans">
        <NavBar activeUnderline={dict.termsTitle} />
        <div className="flex flex-col items-center justify-center text-center py-10 md:py-3 px-4">
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{dict.termsTitle}</h1>
          <Breadcrumb
            homeLabel={dict.breadcrumbHome}
            currentLabel={dict.breadcrumbTerms}
            locale={locale}
          />
        </div>
      </section>

      {/* ===== MAIN CONTENT ===== */}
      <main className="terms-container">
        {/* Sidebar / TopNav */}
        <aside className="terms-sidebar">
          <h2 className="terms-nav-title">
            <ChevronRight size={18} /> Navigation
          </h2>
          <ul className="terms-nav-list">
            {dict.sections.map((section) => (
              <li key={section.id}>
                <button
                  onClick={() =>
                    document
                      .getElementById(section.id)
                      ?.scrollIntoView({ behavior: 'smooth' })
                  }
                  className={`terms-nav-btn ${
                    activeSection === section.id ? 'active' : ''
                  }`}
                >
                  {section.title}
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Terms Content */}
        <div className="terms-content">
          <header className="terms-header">
            <Image
              src="/terms.webp"
              alt="Interacti Marketing Agency"
              width={200}
              height={200}
              className="mx-auto mb-2"
            />
            <p className="terms-intro">{dict.introText}</p>
          </header>

          {dict.sections.map((section) => (
            <section key={section.id} id={section.id} className="terms-section">
              <h3 className="terms-section-title">{section.title}</h3>
              <p>{section.content}</p>
            </section>
          ))}
        </div>
      </main>

      {/* ===== FOOTER ===== */}
      <footer className="text-center py-10 border-t border-gray-200">
        <Image
          src="/logo-purple.webp"
          alt="Interacti Marketing Agency"
          width={80}
          height={80}
          className="mx-auto mb-2"
        />
        <p className="text-sm text-gray-600">
          © 2025 Interacti Marketing Agency. All rights reserved.
        </p>
      </footer>
    </>
  );
}
